// /settings/agents — the Team group's Agents row (docs/09 §4): the archive
// lifecycle surface for the working team. Archived agents are listed from
// `GET /v1/agents?team_id=&archived=true` and restored via
// `POST /v1/agents/{id}:restore`. Permanent deletion has no `/v1` operation,
// so the action renders disabled with a support pointer rather than being
// hidden — the capability is real, the self-serve path is not.
// Settings archetype (§4c): config-width Sections. No field renders without a
// backing /v1 API field (UI-truthfulness).
import { useResolvedWorkingTeamId } from "../../lib/workingScope";
import {
  Button,
  EmptyState,
  Inline,
  InlineError,
  Page,
  Section,
  ShellIcon,
  Skeleton,
  Stack,
  Text,
} from "../../ui";
import { useArchivedAgents } from "../useArchivedAgents";

const PERMANENT_DELETE_HELP =
  "Permanent deletion is not self-serve. Contact support to erase an " +
  "archived agent and its history.";

export function TeamAgentsSettings() {
  const teamId = useResolvedWorkingTeamId();
  const archived = useArchivedAgents({ enabled: true, teamId });
  const agents = (archived.pages ?? []).flatMap((page) => page.items);

  return (
    <Page title="Agents">
      <Section title="Archived agents">
        <Stack gap={3}>
          {archived.isPending ? <Skeleton shape="table-row" /> : null}
          {archived.isError ? (
            <InlineError>Could not load archived agents.</InlineError>
          ) : null}
          {!archived.isPending && !archived.isError && agents.length === 0 ? (
            <EmptyState
              description="Archiving an agent from its settings sends it here."
              title="No archived agents"
            />
          ) : null}
          {agents.map((agent) => (
            <Inline gap={2} key={agent.id}>
              <Text>{agent.display_name}</Text>
              <Button
                disabled={archived.restoringId === agent.id}
                size="sm"
                type="button"
                variant="secondary"
                onClick={() => {
                  archived.restore(agent.id);
                }}
              >
                {archived.restoringId === agent.id ? "Restoring…" : "Restore"}
              </Button>
              {/* Disabled, not hidden: the operation exists, but only
                  support can run it. The adjacent `?` carries the why. */}
              <Button disabled size="sm" type="button" variant="secondary">
                Delete permanently
              </Button>
              <span
                aria-label={PERMANENT_DELETE_HELP}
                className="marker-help-hint"
                title={PERMANENT_DELETE_HELP}
              >
                <ShellIcon name="help" />
              </span>
            </Inline>
          ))}
          {archived.hasNextPage ? (
            <Button
              size="sm"
              type="button"
              variant="secondary"
              onClick={archived.loadMore}
            >
              Load more
            </Button>
          ) : null}
          {archived.restoreError ? (
            <InlineError>{archived.restoreError}</InlineError>
          ) : null}
        </Stack>
      </Section>
    </Page>
  );
}
